import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { select, Store } from '@ngrx/store';
import clone from 'just-clone';
import { combineLatest, Observable, ReplaySubject } from 'rxjs';
import { filter, map, takeWhile, tap } from 'rxjs/operators';
import * as fromLookups from '../../lookups';
import * as fromCommon from '../../shared';
import * as fromShared from '../../shared';
import { LoggingService } from '../../shared/services/logging/logging.service';
import { getUserInfo, IUserInfo } from '../../user-info';
import { IChargeNumberManagement } from '../IChargeNumberManagement';
import { IContractManagement } from '../IContractManagement';
import { IFundingMod } from '../IFundingMod';
import { IInvoiceCycle } from '../IInvoiceCycle';
import { ContractManagementPresentersComponent } from '../presenters/contract-management-presenters.component';
import * as fromCNStoreActions from '../store/charge-number-management.actions';
import * as fromCNStoreReducer from '../store/charge-number-management.reducer';
import * as fromStoreActions from '../store/contract-management.actions';
import * as fromStoreReducer from '../store/contract-management.reducer';
import * as fromFMStoreActions from '../store/funding-mod.actions';
import * as fromFMStoreReducer from '../store/funding-mod.reducer';
import * as fromICStoreActions from '../store/invoice-cycle.actions';
import * as fromICStoreReducer from '../store/invoice-cycle.reducer';
@Component({
    selector: 'ef-contract-management-features',
    templateUrl: './contract-management-features.component.html',
    styleUrls: ['./contract-management-features.component.scss'],
    standalone: false
})
export class ContractManagementFeaturesComponent implements OnInit, OnDestroy {
    @ViewChild(ContractManagementPresentersComponent, { static: true })
    presenter: ContractManagementPresentersComponent;
    process$: Observable<fromShared.Process>;
    public contracts$: Observable<IContractManagement[]>;
    public chargeNumbers$: Observable<IChargeNumberManagement[]>;
    public fundingMods$: Observable<IFundingMod[]>;
    public invoiceCycle$: Observable<IInvoiceCycle[]>;
    public userGroups$: Observable<Array<fromLookups.IUserGroup>>;
    public userInfo$: Observable<IUserInfo>;
    selectedError$: Observable<fromShared.Errors.IShowErrors>;
    selectedErrorWithStatus$: Observable<fromCommon.IStatusMessage>;

    ShowError = new ReplaySubject<fromShared.Errors.IShowErrors>(1);
    active = true;
    activeContractsOnly = true;
    activeCNsOnly = true;
    activeFMsOnly = true;
    private pendingFundingModRefresh = false;
    constructor(
        private log: LoggingService,
        private store: Store<fromStoreReducer.State>,
        private cnStore: Store<fromCNStoreReducer.State>,
        private fmStore: Store<fromFMStoreReducer.State>,
        private icStore: Store<fromICStoreReducer.State>,
        private lookupsStore: Store<fromLookups.LookupState>
    ) {}

    ngOnInit() {
        this.icStore.dispatch(fromICStoreActions.getInvoiceCycle());
        this.store.dispatch(
            fromStoreActions.getContracts({ Activeonly: true })
        );
        this.store.dispatch(new fromLookups.LoadUserGroups());
        this.setupObservables();
        this.setupSubscriptions();
    }
    ngOnDestroy() {
        this.active = false;
        this.store.dispatch(fromStoreActions.clearContractManagements());
        this.cnStore.dispatch(
            fromCNStoreActions.clearChargeNumberManagements()
        );
        this.fmStore.dispatch(fromFMStoreActions.clearFundingMods());
    }
    setupObservables() {
        this.process$ = this.store.pipe(select(fromShared.selectCurrent));
        this.userInfo$ = this.store.pipe(select(getUserInfo));
        this.contracts$ = this.store.pipe(
            select(fromStoreReducer.selectAll),
            map(contracts => clone(contracts))
        );
        this.chargeNumbers$ = this.cnStore.pipe(
            select(fromCNStoreReducer.selectAll),
            map(chargenumbers => clone(chargenumbers))
        );
        this.fundingMods$ = this.fmStore.pipe(
            select(fromFMStoreReducer.selectAll),
            map(fundingmods => clone(fundingmods))
        );
        this.invoiceCycle$ = this.icStore.pipe(
            select(fromICStoreReducer.selectAll)
        );
        this.userGroups$ = this.lookupsStore.pipe(
            select(fromLookups.getUserGroupsLookup)
        );

        this.selectedError$ = this.store.pipe(
            select(fromShared.Errors.getShowErrors),
            tap(selected =>
                this.log.verbose({
                    type: 'ContractManagementFeatureComponent:selectedError$',
                    selected
                })
            )
        );
        const errors$ = this.store.pipe(select(fromShared.Errors.getErrors));
        this.selectedErrorWithStatus$ = combineLatest([
            this.selectedError$,
            errors$,
            this.contracts$.pipe(filter(x => !!x)),
        ]).pipe(
            tap(([selected, errors]) =>
                this.log.verbose({
                    type: 'ContractManagementFeatureComponent:errors$',
                    errors,
                    selected
                })
            ),
            map(
                ([selected, errors]) =>
                    errors.filter(
                        e =>
                            e.MsgEntityID === selected.MsgEntityID &&
                            e.MsgProcessID === selected.MsgProcessID
                    )[0]
            ),
            tap(selected =>
                this.log.verbose({
                    type: 'ContractManagementFeatureComponent:errors$:filtered',
                    selected
                })
            )
        );
    }
    setupSubscriptions() {
        combineLatest([this.process$, this.ShowError])
            .pipe(takeWhile(() => this.active))
            .subscribe(([process, event]) => {
                this.store.dispatch(
                    new fromShared.Errors.SetShowErrors({
                        open: event.open,
                        MsgEntityID: event.MsgEntityID,
                        MsgProcessID: process.ProcessID
                    })
                );
            });
        this.presenter.contractSelectedEvent
            .pipe(takeWhile(_ => this.active))
            .subscribe(value => {
                this.cnStore.dispatch(
                    fromCNStoreActions.getChargeNumbers({ Contract: value })
                );
            });

        this.presenter.chargeNumbersSelectedEvent
            .pipe(takeWhile(_ => this.active))
            .subscribe(value => {
                this.fmStore.dispatch(
                    fromFMStoreActions.getFundingMod({ contract: value })
                );
            });

        this.fundingMods$
            .pipe(takeWhile(() => this.active))
            .subscribe(() => {
                if (!this.pendingFundingModRefresh) {
                    return;
                }
                const contractId =
                    this.presenter?.selectedContract?.ContractListID;
                if (contractId) {
                    this.store.dispatch(
                        fromStoreActions.getContracts({
                            Activeonly: this.activeContractsOnly
                        })
                    );
                    setTimeout(() => {
                        this.cnStore.dispatch(
                            fromCNStoreActions.getChargeNumbers({
                                Contract: {
                                    ContractListID: contractId,
                                    Activeonly: !!this.activeCNsOnly
                                }
                            })
                        );
                    }, 250);
                }
                this.pendingFundingModRefresh = false;
            });
    }
    onSaveContract($event) {
        this.store.dispatch(
            fromStoreActions.putContracts({
                contracts: [$event],
                Activeonly: this.activeContractsOnly
            })
        );
    }
    onSaveChargeNumber($event) {
        this.cnStore.dispatch(
            fromCNStoreActions.putChargeNumbers({ chargeNumbers: [$event] })
        );
    }
    onSaveFundingMod($event) {
        this.fmStore.dispatch(
            fromFMStoreActions.putFundingMod({ fundingMod: [$event] })
        );
        this.pendingFundingModRefresh = true;
    }
    onDeleteFundingMod($event) {
        this.fmStore.dispatch(
            fromFMStoreActions.removeFundingMod({ ids: [$event.FundingID] })
        );
    }
    onToggleInactiveContracts($event) {
        this.activeContractsOnly = $event;
        this.store.dispatch(
            fromStoreActions.getContracts({ Activeonly: $event })
        );
    }
    onToggleInactiveCNs($event) {
        this.activeCNsOnly = $event.Activeonly;
        this.cnStore.dispatch(
            fromCNStoreActions.getChargeNumbers({ Contract: $event })
        );
    }
    onToggleInactiveFMs($event) {
        this.activeFMsOnly = $event.Activeonly;
        this.fmStore.dispatch(
            fromFMStoreActions.getFundingMod({ contract: $event })
        );
    }
    onShowError(event) {
        this.ShowError.next(event);
    }
}
